import { useEffect, useState } from 'react';
import { errorText } from '../api';
import { WEEKDAYS } from '../format';
import { adminApi, type Rule } from './api';

/**
 * Wochenraster einer Terminart: je Wochentag beliebig viele Zeitfenster („Di 10:00–12:00").
 * Gespeichert wird immer die ganze Liste.
 */
export function RulesEditor({ eventTypeId }: { eventTypeId: string }) {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    adminApi
      .listRules(eventTypeId)
      .then((r) => setRules(r.rules.map(({ weekday, start, end }) => ({ weekday, start, end }))))
      .catch((e) => setMessage({ kind: 'error', text: errorText(e) }));
  }, [eventTypeId]);

  if (!rules) return <p className="muted small">Lade Wochenraster …</p>;

  const update = (index: number, patch: Partial<Rule>) => setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const remove = (index: number) => setRules(rules.filter((_, i) => i !== index));
  const add = (weekday: number) => {
    const last = rules.filter((r) => r.weekday === weekday).at(-1);
    setRules([...rules, { weekday, start: last?.end ?? '09:00', end: last ? addHour(last.end) : '12:00' }]);
  };

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const saved = await adminApi.putRules(eventTypeId, rules!);
      setRules(saved.rules.map(({ weekday, start, end }) => ({ weekday, start, end })));
      setMessage({ kind: 'ok', text: 'Gespeichert.' });
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rules">
      <div className="field">
        <label>Wochenraster</label>
        <span className="muted small">An welchen Wochentagen zu welchen Zeiten gebucht werden kann. Daraus entstehen Termine im Takt der Dauer.</span>
      </div>
      {WEEKDAYS.map((name, i) => {
        const weekday = i + 1;
        const own = rules.map((r, index) => ({ r, index })).filter(({ r }) => r.weekday === weekday);
        return (
          <div key={weekday} className="rules__day">
            <span className={`rules__name${own.length ? '' : ' muted'}`}>{name}</span>
            <div className="rules__windows">
              {own.length === 0 && <span className="faint small">–</span>}
              {own.map(({ r, index }) => (
                <span key={index} className="rules__window">
                  <input type="time" step={300} value={r.start} onChange={(e) => update(index, { start: e.target.value })} />
                  <span>–</span>
                  <input type="time" step={300} value={r.end} onChange={(e) => update(index, { end: e.target.value })} />
                  <button type="button" className="link-button" title="Zeitfenster entfernen" onClick={() => remove(index)}>
                    ✕
                  </button>
                </span>
              ))}
              <button type="button" className="link-button" onClick={() => add(weekday)}>
                + Zeit
              </button>
            </div>
          </div>
        );
      })}
      <div className="save-bar">
        <button type="button" className="button button--small" disabled={busy} onClick={() => void save()}>
          Wochenraster speichern
        </button>
        {message && <span className={message.kind === 'ok' ? 'success-inline' : 'error-inline'}>{message.text}</span>}
      </div>
    </div>
  );
}

function addHour(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  return `${String(Math.min(23, h + 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
